import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { getRequest, postRequest, patchRequest } from "@react/helpers/fetch"
import Modal  from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import Badge  from "@react/shared/ui/Badge"

const RESULTS = [
  { value: "positive", icon: "check",       active: "border-emerald-500 bg-emerald-50 text-emerald-700" },
  { value: "negative", icon: "xmark",       active: "border-rose-500 bg-rose-50 text-rose-700" },
  { value: "comment",  icon: "comment-dots", active: "border-sky-500 bg-sky-50 text-sky-700" },
]

const HISTORY_DOT = {
  positive:  "bg-emerald-500",
  negative:  "bg-rose-500",
  comment:   "bg-sky-500",
  requested: "bg-brand-500",
  reminded:  "bg-amber-500",
}

const HISTORY_TONE = { positive: "success", negative: "danger", comment: "blue", requested: "brand", reminded: "warning" }

const STEP_CIRCLE = {
  done:     "bg-emerald-500 text-white",
  current:  "bg-brand-500 text-white ring-4 ring-brand-500/20",
  upcoming: "bg-white text-slate-400 border border-slate-300",
  skipped:  "bg-slate-100 text-slate-300 border border-dashed border-slate-300",
}

const t = (key, options) => I18n.t(`validation.workflow.${key}`, options)

const Section = ({ title, aside, children }) => (
  <section className="rounded-xl border border-slate-200 bg-white">
    <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-100">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</h3>
      {aside}
    </div>
    <div className="p-4">{children}</div>
  </section>
)

const DetailRow = ({ label, value }) => value ? (
  <div className="flex justify-between gap-3 text-sm py-1">
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-900 font-medium text-right">{value}</span>
  </div>
) : null

const ValidationModal = ({ isOpen, onClose, url, onChange, launchBlockedReason = null }) => {
  const [data, setData]             = useState(null)
  const [result, setResult]         = useState("positive")
  const [comment, setComment]       = useState("")
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen || !url) return
    setData(null)
    setResult("positive")
    setComment("")
    getRequest(url)
      .then(response => {
        if (response?.badge) return setData(response)
        flashAlert(response?.error || I18n.t("quotes.builder.footer.network_error"))
        onClose()
      })
      .catch(() => { flashAlert(I18n.t("quotes.builder.footer.network_error")); onClose() })
  }, [isOpen, url])

  const handle = request => {
    setSubmitting(true)
    request
      .then(response => {
        if (response?.validation) {
          setData(response.validation)
          onChange?.(response.validation)
        }
        if (response?.error) return flashAlert(response.error)
        if (response?.notice) flashNotice(response.notice)
        setComment("")
      })
      .catch(() => flashAlert(I18n.t("quotes.builder.footer.network_error")))
      .finally(() => setSubmitting(false))
  }

  const submitReview = () => {
    if (result === "comment" && !comment.trim()) return
    handle(postRequest({ url: data.urls.review, body: { result, comment } }))
  }
  const remind = () => handle(postRequest({ url: data.urls.remind }))
  const launch = () => handle(patchRequest({ url: data.urls.launch }))

  const commentMissing = result === "comment" && !comment.trim()

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      size    = "lg"
      eyebrow = {t("modal_title")}
      title   = {
        <span className="flex items-center gap-3 flex-wrap">
          <span>{data?.title || "…"}</span>
          {data && <Badge tone={data.badge.tone} variant="soft" size="sm">{data.badge.label}</Badge>}
        </span>
      }
      footer  = {
        <>
          <Button variant="ghost" onClick={onClose}>{I18n.t("button.close")}</Button>
          <div className="flex items-center gap-2">
            {data?.can_remind && (
              <Button variant="secondary" onClick={remind} disabled={submitting}>
                <FontAwesomeIcon icon="bell" /> {t("remind")}
              </Button>
            )}
            {data?.can_launch && (
              <Button variant="primary" onClick={launch} disabled={submitting || !!launchBlockedReason} title={launchBlockedReason || ""}>
                <FontAwesomeIcon icon="paper-plane" /> {t("launch_approval")}
              </Button>
            )}
            {data?.can_review && (
              <Button
                variant  = {result === "negative" ? "danger" : "primary"}
                onClick  = {submitReview}
                disabled = {submitting || commentMissing}
              >
                <FontAwesomeIcon icon={submitting ? "spinner" : "check"} spin={submitting} /> {t("submit_review")}
              </Button>
            )}
          </div>
        </>
      }
    >
      {!data ? (
        <div className="flex justify-center py-12 text-slate-400">
          <FontAwesomeIcon icon="spinner" spin size="2x" />
        </div>
      ) : (
        <>
          {(data.amount || data.reminder_at) && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              {data.amount && (
                <div className="text-2xl font-semibold tabular-nums text-slate-900">{data.amount}</div>
              )}
              {data.reminder_at && (
                <div className="inline-flex items-center gap-2 rounded-lg bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200 px-3 py-1.5 text-sm">
                  <FontAwesomeIcon icon="clock" /> {t("reminder_banner", { time: data.reminder_at })}
                </div>
              )}
            </div>
          )}

          {data.can_review ? (
            <section className="rounded-xl border border-brand-200 bg-brand-50/40 p-4 space-y-3">
              <div className="grid grid-cols-3 gap-2">
                {RESULTS.map(option => (
                  <button
                    key       = {option.value}
                    type      = "button"
                    onClick   = {() => setResult(option.value)}
                    className = {`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${result === option.value ? option.active : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
                  >
                    <FontAwesomeIcon icon={option.icon} /> {t(`reviews.result.${option.value}`)}
                  </button>
                ))}
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700">
                  {result === "comment" ? t("reviews.comment") : t("reviews.comment_label")}
                </label>
                <textarea
                  rows        = {3}
                  value       = {comment}
                  onChange    = {e => setComment(e.target.value)}
                  placeholder = {t("reviews.comment_placeholder")}
                  className   = "w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
            </section>
          ) : (
            <p className="text-sm text-slate-500">{t("cannot_review_here")}</p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section title={t("details.title")}>
              <DetailRow label={t("details.requester")} value={data.details.requester} />
              <DetailRow label={t("details.created")}   value={data.details.created} />
              <DetailRow label={t("details.submitted")} value={data.details.submitted} />
            </Section>

            <Section
              title = {t("progress.title")}
              aside = {data.workflow && <span className="text-sm font-semibold text-slate-700 truncate">{data.workflow.name}</span>}
            >
              {!data.workflow ? (
                <p className="text-sm text-slate-500">{t("progress.no_workflow")}</p>
              ) : (
                <div className="space-y-3">
                  {data.workflow.selected_because && (
                    <p className="text-xs text-slate-500">{data.workflow.selected_because}</p>
                  )}
                  {!data.workflow.self_approval && (
                    <p className="flex items-center gap-1.5 text-xs text-slate-500">
                      <FontAwesomeIcon icon="info-circle" /> {t("progress.self_approval_not_allowed")}
                    </p>
                  )}
                  {data.workflow.skipped_count > 0 && (
                    <p className="flex items-center gap-1.5 text-xs text-slate-500">
                      <FontAwesomeIcon icon="info-circle" /> {t("progress.steps_skipped", { count: data.workflow.skipped_count })}
                    </p>
                  )}
                  <ol className="relative space-y-3 before:absolute before:left-[13px] before:top-2 before:bottom-2 before:w-px before:bg-slate-200">
                    {data.steps.map(step => (
                      <li key={step.id} className={`relative flex items-start gap-3 ${step.state === "skipped" ? "opacity-60" : ""}`}>
                        <span className={`relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${STEP_CIRCLE[step.state]}`}>
                          {step.state === "done" ? <FontAwesomeIcon icon="check" /> : step.position}
                        </span>
                        <div className="min-w-0 pt-0.5">
                          <div className={`text-sm font-medium ${step.state === "current" ? "text-brand-700" : "text-slate-900"}`}>{step.name}</div>
                          {(step.skip_reason || step.range) && (
                            <div className="text-xs text-slate-500">
                              {[step.skip_reason, step.range].filter(Boolean).join(" · ")}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </Section>
          </div>

          {data.current_step && (
            <Section title={t("eligibility.title", { step: data.current_step.name })}>
              <ul className="divide-y divide-slate-100">
                {data.current_step.approvers.map(approver => (
                  <li key={approver.id} className="flex items-center gap-3 py-2">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                      {approver.initials}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 truncate">{approver.name}</div>
                      <div className="text-xs text-slate-500 truncate">{approver.email}</div>
                    </div>
                    {approver.limit && <span className="text-xs text-slate-500 tabular-nums">{approver.limit}</span>}
                    <Badge tone={approver.can_approve ? "success" : "neutral"} variant="soft" size="sm">
                      <FontAwesomeIcon icon={approver.can_approve ? "shield-alt" : "ban"} className="mr-1" />
                      {t(`eligibility.${approver.can_approve ? "can_approve" : "cannot_approve"}`)}
                    </Badge>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <Section title={t("history.title")}>
            {data.reviews.length === 0 ? (
              <p className="text-sm text-slate-500">{t("no_reviews_yet")}</p>
            ) : (
              <ol className="space-y-3">
                {data.reviews.map(review => (
                  <li key={review.id} className="flex items-start gap-3">
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${HISTORY_DOT[review.result] || "bg-slate-400"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-medium text-slate-900">{review.reviewer}</span>
                        <Badge tone={HISTORY_TONE[review.result] || "neutral"} variant="soft" size="xs">{review.result_label}</Badge>
                        {review.step && <span className="text-slate-500">· {review.step}</span>}
                      </div>
                      {review.comment && (
                        <p className="mt-1 text-sm text-slate-600 italic">“{review.comment}”</p>
                      )}
                    </div>
                    <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">{review.date}</span>
                  </li>
                ))}
              </ol>
            )}
          </Section>
        </>
      )}
    </Modal>
  )
}

export default ValidationModal
